<template>
  <div class="container mt-4">
    <!-- Encabezado y acciones -->
    <div class="row g-2 align-items-center mb-4 flex-wrap">
      <div class="col-12 col-md-auto mb-2 mb-md-0 d-flex justify-content-between align-items-center">
        <button @click="redirectNuevoItem" class="btn btn-success w-100 w-md-auto">
          <i class="fas fa-plus me-2"></i> Nuevo Ítem
        </button>
      </div>
      <div class="col-12 col-md flex-grow-1">
        <input
          type="text"
          class="form-control"
          placeholder="Buscar ítem..."
          v-model="searchTerm"
        />
      </div>
    </div>

    <!-- Vista tabla para desktop/tablet -->
    <div class="table-responsive d-none d-md-block" v-if="paginatedInventario.length > 0 && vistaActivada === 'inventario'">
      <table class="table table-hover align-middle">
        <thead class="table-success">
          <tr>
            <th @click="sortTable('categoria')">Categoría <span v-if="sortColumn === 'categoria'">{{ sortDirection }}</span></th>
            <th @click="sortTable('nombre')">Nombre <span v-if="sortColumn === 'nombre'">{{ sortDirection }}</span></th>
            <th @click="sortTable('cantidad')">Cantidad <span v-if="sortColumn === 'cantidad'">{{ sortDirection }}</span></th>
            <th @click="sortTable('nivel_minimo')">Nivel Mínimo <span v-if="sortColumn === 'nivel_minimo'">{{ sortDirection }}</span></th>
            <th @click="sortTable('unidad_de_medida')">Unidad de Medida <span v-if="sortColumn === 'unidad_de_medida'">{{ sortDirection }}</span></th>
            <th @click="sortTable('costo_unitario_usd')">Costo Unitario <span v-if="sortColumn === 'costo_unitario_usd'">{{ sortDirection }}</span></th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="item in paginatedInventarioWithWarning" :key="item.id">
            <tr v-if="!item.__isWarning">
              <td>{{ item.categoria }}</td>
              <td>{{ item.nombre }}</td>
              <td>
                <input
                  type="number"
                  class="form-control"
                  v-model="item.cantidad"
                  :disabled="!item.isEditing"
                  :class="{ 'is-warning': item.cantidad < item.nivel_minimo }"
                />
                <font-awesome-icon
                  v-if="item.cantidad < item.nivel_minimo"
                  icon="exclamation-triangle"
                  class="text-danger ms-2"
                  title="Cantidad menor que el nivel mínimo"
                />
              </td>
              <td>
                <input
                  type="number"
                  class="form-control"
                  v-model="item.nivel_minimo"
                  :disabled="!item.isEditing"
                />
              </td>
              <td>{{ item.unidad_de_medida }}</td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  class="form-control"
                  v-model="item.costo_unitario_usd"
                  :disabled="!item.isEditing"
                />
              </td>
              <td>
                <div v-if="item.isEditing" class="d-flex justify-content-around">
                  <button class="btn btn-success btn-sm" @click="guardarCambios(item)">Guardar</button>
                  <button class="btn btn-secondary btn-sm" @click="cancelarEdicion(item)">Cancelar</button>
                </div>
                <div v-else>
                  <button class="btn btn-primary btn-sm" @click="editarItem(item)">Editar</button>
                  <button class="btn btn-danger btn-sm" @click="eliminarItem(item)"><i class="fas fa-trash"></i></button>
                </div>
              </td>
            </tr>
            <tr v-else>
              <td colspan="7" class="p-0">
                <div class="alert alert-warning text-center mb-0 py-2">
                  <strong>Atención:</strong> Si deseas cambiar la <b>categoría</b>, el <b>nombre</b> o la <b>unidad de medida</b> de un ítem, debes eliminarlo y crear uno nuevo. Esto es para mantener la trazabilidad y consistencia del inventario.
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <!-- Paginación desktop -->
      <nav aria-label="Page navigation" class="mt-3">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="prevPage">Anterior</button>
          </li>
          <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: page === currentPage }">
            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="nextPage">Siguiente</button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Vista tipo card para mobile -->
    <div class="d-block d-md-none" v-if="paginatedInventario.length > 0 && vistaActivada === 'inventario'">
      <div v-for="item in paginatedInventario" :key="item.id" class="card mb-3 shadow-sm">
        <div class="card-body p-3">
          <div v-if="item.isEditing" class="alert alert-warning text-center mb-2 py-2">
            <strong>Atención:</strong> Si deseas cambiar la <b>categoría</b>, el <b>nombre</b> o la <b>unidad de medida</b> de un ítem, debes eliminarlo y crear uno nuevo. Esto es para mantener la trazabilidad y consistencia del inventario.
          </div>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="card-title mb-0">{{ item.nombre }}</h5>
            <span class="badge bg-success">{{ item.categoria }}</span>
          </div>
          <div class="mb-2">
            <strong>Cantidad:</strong>
            <input
              type="number"
              class="form-control form-control-sm d-inline-block w-auto ms-2"
              v-model="item.cantidad"
              :disabled="!item.isEditing"
              :class="{ 'is-warning': item.cantidad < item.nivel_minimo }"
              style="max-width: 90px;"
            />
            <font-awesome-icon
              v-if="item.cantidad < item.nivel_minimo"
              icon="exclamation-triangle"
              class="text-danger ms-2"
              title="Cantidad menor que el nivel mínimo"
            />
          </div>
          <div class="mb-2">
            <strong>Nivel Mínimo:</strong>
            <input
              type="number"
              class="form-control form-control-sm d-inline-block w-auto ms-2"
              v-model="item.nivel_minimo"
              :disabled="!item.isEditing"
              style="max-width: 90px;"
            />
          </div>
          <div class="mb-2">
            <strong>Unidad:</strong> {{ item.unidad_de_medida }}
          </div>
          <div class="mb-2">
            <strong>Costo Unitario:</strong>
            <input
              type="number"
              step="0.01"
              class="form-control form-control-sm d-inline-block w-auto ms-2"
              v-model="item.costo_unitario_usd"
              :disabled="!item.isEditing"
              style="max-width: 90px;"
            />
          </div>
          <div class="d-flex justify-content-end gap-2 mt-2">
            <template v-if="item.isEditing">
              <button class="btn btn-success btn-sm flex-fill" @click="guardarCambios(item)">Guardar</button>
              <button class="btn btn-secondary btn-sm flex-fill" @click="cancelarEdicion(item)">Cancelar</button>
            </template>
            <template v-else>
              <button class="btn btn-primary btn-sm flex-fill" @click="editarItem(item)">Editar</button>
              <button class="btn btn-danger btn-sm flex-fill" @click="eliminarItem(item)"><i class="fas fa-trash"></i></button>
            </template>
          </div>
        </div>
      </div>
      <!-- Paginación mobile -->
      <nav aria-label="Page navigation" class="mt-3">
        <ul class="pagination pagination-sm justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="prevPage">Anterior</button>
          </li>
          <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: page === currentPage }">
            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="nextPage">Siguiente</button>
          </li>
        </ul>
      </nav>
    </div>

    <div v-else-if="vistaActivada === 'nuevoItem'">
      <altaInventarioItem :proveedor_id="proveedor_id" @itemCreado="onItemCreado" @cancelarCreacionItem="cancelarCreacionItem" />
    </div>
    <div v-else-if="vistaActivada === 'nuevaOrdenCompra'">
      <altaOrdenDeCompra :proveedor_id="proveedor_id" @ordenConfirmada="onOrdenConfirmada" @cancelarCreacionOrden="cancelarCreacionOrden" />
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import altaInventarioItem from './alta-InventarioItem.vue';
import altaOrdenDeCompra from './alta-ordenDeCompra.vue';

export default {
  components: {
    altaInventarioItem,
    altaOrdenDeCompra,
  },
  props: {
    proveedor_id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      inventario: [],
      sortColumn: '',
      sortDirection: '↑',
      searchTerm: '',
      backupInventario: {},
      vistaActivada: 'inventario',
      currentPage: 1,
      itemsPerPage: 10,
      clubActual: null,
    };
  },
  computed: {
    filteredInventario() {
      return this.sortedInventario.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(this.searchTerm.toLowerCase())
        )
      );
    },
    sortedInventario() {
      const sorted = [...this.inventario];
      if (this.sortColumn) {
        sorted.sort((a, b) => {
          let aValue = a[this.sortColumn];
          let bValue = b[this.sortColumn];
          if (aValue < bValue) return this.sortDirection === '↑' ? -1 : 1;
          if (aValue > bValue) return this.sortDirection === '↑' ? 1 : -1;
          return 0;
        });
      }
      return sorted;
    },
    paginatedInventario() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredInventario.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredInventario.length / this.itemsPerPage);
    },
    paginatedInventarioWithWarning() {
      // Devuelve un array donde después de cada item en edición, agrega un objeto especial para la advertencia
      const result = [];
      for (const item of this.paginatedInventario) {
        result.push(item);
        if (item.isEditing) {
          result.push({ __isWarning: true, id: 'warn-' + item.id });
        }
      }
      return result;
    },
  },
  watch: {
    proveedor_id: {
      immediate: true,
      handler(newVal) {
        this.fetchItemsInventario(newVal);
      },
    },
  },
  mounted() {
    this.clubActual = this.$store.getters.getClubActual;
  },
  methods: {
    async fetchItemsInventario(proveedor_id) {
      try {
        this.clubActual = this.$store.getters.getClubActual;


        const response = await axios.get(`/api/inventario/${proveedor_id}`, {
          params: { club_id: this.clubActual.id },
        });
        this.inventario = response.data.map((item) => ({
          ...item,
          isEditing: false,
        }));
      } catch (error) {
        console.error('Error al obtener ítems del proveedor:', error);
      }
    },
    sortTable(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === '↑' ? '↓' : '↑';
      } else {
        this.sortColumn = column;
        this.sortDirection = '↑';
      }
    },
    redirectNuevoItem() {
      this.vistaActivada = this.vistaActivada === 'nuevoItem' ? 'inventario' : 'nuevoItem';
    },
    redirectAltaOrdenCompra() {
      this.vistaActivada = this.vistaActivada === 'nuevaOrdenCompra' ? 'inventario' : 'nuevaOrdenCompra';
    },
    editarItem(item) {
      this.backupInventario[item.id] = { ...item };
      item.isEditing = true;
    },
    cancelarEdicion(item) {
      Object.assign(item, this.backupInventario[item.id]);
      item.isEditing = false;
    },
    async guardarCambios(item) {
      try {

        await axios.put(`/api/inventario_editar/${item.id}`, {form:{
          cantidad: item.cantidad,
          nivel_minimo: item.nivel_minimo,
          costo_unitario_usd: item.costo_unitario_usd,
        }, club_id: this.clubActual.id});
        alert('Cambios guardados exitosamente');
        item.isEditing = false;
      } catch (error) {
        console.error('Error al guardar los cambios:', error);
        alert('Hubo un error al guardar los cambios.');
      }
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    goToPage(page) {
      this.currentPage = page;
    },
    onItemCreado(nuevoItem) {
   
      this.inventario.push(nuevoItem);
      this.vistaActivada = 'inventario';
    },
    onOrdenConfirmada(nuevaOrden) {
      console.log('nuevaOrden', nuevaOrden);

      this.vistaActivada = 'inventario';
    },
    cancelarCreacionItem() {
      this.vistaActivada = 'inventario';
    },
    cancelarCreacionOrden() {
      this.vistaActivada = 'inventario';
    },
    async eliminarItem(item) {
      if (confirm('¿Estás seguro de que deseas eliminar este ítem? Esta acción no se puede deshacer.')) {
        try {
          await axios.delete(`/api/inventario/${item.id}`, { data: { club_id: this.clubActual.id } });
          this.inventario = this.inventario.filter(i => i.id !== item.id);
          alert('Ítem eliminado correctamente.');
        } catch (error) {
          console.error('Error al eliminar el ítem:', error);
          alert('Hubo un error al eliminar el ítem.');
        }
      }
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 1200px;
}

.table {
  width: 100%;
  font-size: 1rem;
}

th, td {
  padding: 12px;
  vertical-align: middle;
}

th {
  cursor: pointer;
  background-color: #dff0d8;
}

th:hover {
  background-color: #c8e6c9;
}

tr:hover {
  background-color: #e8f5e9;
}

.table-responsive {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.pagination .page-item.active .page-link {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.text-danger {
  color: #dc3545;
}

.form-control {
  text-align: center;
}

.is-warning {
  background-color: #fff3cd;
}

/* Mejoras responsivas y mobile */
@media (max-width: 767.98px) {
  .container {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  .btn, .form-control, .form-control-sm {
    font-size: 1.1rem;
    min-height: 44px;
  }
  .btn-sm {
    min-height: 38px;
    font-size: 1rem;
  }
  .card-title {
    font-size: 1.15rem;
  }
  .card {
    border-radius: 1rem;
    border: 1px solid #e0e0e0;
  }
  .pagination {
    font-size: 1rem;
  }
  .alert {
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }
}

/* Mejorar contraste y feedback visual */
.card {
  border-radius: 0.75rem;
  border: 1px solid #e0e0e0;
}
.card-title {
  font-weight: 600;
}
.badge.bg-success {
  font-size: 0.95rem;
  padding: 0.5em 0.8em;
}

/* Inputs en edición */
input[disabled] {
  background: #f8f9fa !important;
  color: #888 !important;
}
</style>
